import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  TrendingUp,
  UtensilsCrossed,
  Dumbbell,
  Lock,
  Play,
  CalendarPlus,
  CheckCircle2,
  ChevronRight,
  BookOpen,
  Crown,
  UserCheck,
  Sparkles,
  Zap,
  Flame,
  Check,
  ArrowUpRight,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { isPaidTier, normaliseTier } from "@/lib/membershipUtils";
import { useState, useMemo } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { todayLocalDateStr } from "@/lib/dateUtils";
import { useActiveWorkout } from "@/lib/ActiveWorkoutContext";
import { subDays, format } from "date-fns";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { QuickProgressDialog } from "@/components/dashboard/QuickProgressLog";
import QuickMealLogForm from "@/components/dashboard/QuickMealLogForm";
import CheckInCalendarModal from "@/components/dashboard/CheckInCalendarModal";
import { BRAND_MONOGRAM } from "@/lib/brandAssets";

const SESSION_TIERS = new Set(["strength", "elite_coaching"]);

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function calcStreak(checkIns, userId) {
  if (!checkIns || !checkIns.length) return 0;
  const userCheckins = checkIns.filter((c) => c.user_id === userId);
  if (!userCheckins.length) return 0;
  const checkedDates = new Set(
    userCheckins.map((c) => c.checked_in_date).filter(Boolean)
  );
  if (!checkedDates.size) return 0;
  let streak = 0;
  let current = new Date();
  const todayKey = format(current, "yyyy-MM-dd");
  if (!checkedDates.has(todayKey)) {
    current = subDays(current, 1);
  }
  while (checkedDates.has(format(current, "yyyy-MM-dd"))) {
    streak++;
    current = subDays(current, 1);
  }
  return streak;
}

const leftItems = [
  { path: "/", label: "Home", icon: Home, alwaysFree: true },
  { path: "/progress", label: "Progress", icon: TrendingUp },
];

const rightItems = [
  { path: "/workouts", label: "Workouts", icon: Dumbbell },
  { path: "/meals", label: "Meals", icon: UtensilsCrossed },
];

export default function BottomNav({ forceVisible = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { active: activeWorkout, resumeWorkout, startWorkout } = useActiveWorkout();
  const isPaid = isPaidTier(user?.membership_type);
  const tier = normaliseTier(user?.membership_type);

  const canBookSessions =
    SESSION_TIERS.has(tier) || user?.role === "admin" || user?.role === "trainer";
  const canCheckIn =
    tier === "elite_coaching" || user?.role === "admin" || user?.role === "trainer";
  const canUseAIMacros =
    tier === "strength" || tier === "elite_coaching" || user?.role === "admin" || user?.role === "trainer";

  const [sheetOpen, setSheetOpen] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [mealOpen, setMealOpen] = useState(false);
  const [checkInHistoryOpen, setCheckInHistoryOpen] = useState(false);
  const queryClient = useQueryClient();
  const todayDate = todayLocalDateStr();

  // Check-in status & streak
  const { data: checkIns = [] } = useQuery({
    queryKey: ["checkins"],
    queryFn: () => base44.entities.CheckIn.list("-checked_in_date", 200),
    enabled: !!user,
  });

  const checkedInToday = checkIns.some(
    (c) => c.user_id === user?.id && c.checked_in_date === todayDate
  );

  const streak = useMemo(
    () => calcStreak(checkIns, user?.id),
    [checkIns, user?.id]
  );

  const checkedDates = useMemo(() => {
    return new Set(
      checkIns
        .filter((c) => c.user_id === user?.id)
        .map((c) => c.checked_in_date)
        .filter(Boolean)
    );
  }, [checkIns, user?.id]);

  // Today's workout plan resolution
  const todayName = DAYS[new Date().getDay()];
  const todayKey = todayName.toLowerCase();

  const { data: weeklyPlan = null } = useQuery({
    queryKey: ["userWeeklyWorkoutPlan", user?.id],
    queryFn: () =>
      base44.entities.UserWeeklyWorkoutPlan.filter({ user_id: user?.id }).then(
        (results) => results[0] || null
      ),
    enabled: !!user,
  });

  const { data: workouts = [] } = useQuery({
    queryKey: ["workouts"],
    queryFn: () => base44.entities.WorkoutPlan.list(),
    enabled: !!user,
  });

  const { data: templates = [] } = useQuery({
    queryKey: ["workout-templates"],
    queryFn: () => base44.entities.WorkoutTemplate.list("-created_date"),
    enabled: !!user,
  });

  const todayPlanId = weeklyPlan ? weeklyPlan[todayKey] : null;

  const todayPlan = useMemo(() => {
    if (!todayPlanId) return null;
    const fromWorkouts = workouts.find(
      (w) =>
        w.id === todayPlanId ||
        w._id === todayPlanId ||
        String(w.id || w._id) === String(todayPlanId)
    );
    if (fromWorkouts) return fromWorkouts;
    const fromTemplates = templates.find(
      (t) =>
        t.id === todayPlanId ||
        t._id === todayPlanId ||
        String(t.id || t._id) === String(todayPlanId)
    );
    if (fromTemplates) {
      return {
        id: fromTemplates.id || fromTemplates._id,
        name: fromTemplates.name,
        exercises: fromTemplates.exercises || [],
        body_part_category: fromTemplates.body_part_category,
        goal: fromTemplates.goal,
      };
    }
    return null;
  }, [todayPlanId, workouts, templates]);

  const renderItem = (item) => {
    const isActive = location.pathname === item.path;
    const locked = !item.alwaysFree && item.path !== "/" && !isPaid;
    if (locked) {
      return (
        <span
          key={item.path}
          aria-disabled="true"
          title="Upgrade your membership to unlock"
          data-testid={`bottomnav-${item.label.toLowerCase()}-locked`}
          className="flex flex-col items-center gap-0.5 flex-1 py-2 rounded-xl relative text-sidebar-foreground/30 cursor-not-allowed"
        >
          <item.icon className="w-6 h-6" />
          <span className="text-[11px] font-medium">{item.label}</span>
          <Lock className="w-2.5 h-2.5 absolute top-1.5 right-1.5 opacity-60" />
        </span>
      );
    }
    return (
      <Link
        key={item.path}
        to={item.path}
        data-testid={`bottomnav-${item.label.toLowerCase()}`}
        className={`flex flex-col items-center gap-0.5 flex-1 py-2 rounded-xl transition-all relative ${
          isActive
            ? "text-sky-400"
            : "text-sidebar-foreground/60 hover:text-sidebar-foreground"
        }`}
      >
        <item.icon className="w-6 h-6" strokeWidth={isActive ? 2.5 : 2} />
        <span className="text-[11px] font-medium">{item.label}</span>
      </Link>
    );
  };

  const handleAction = (path, opts = {}) => {
    setSheetOpen(false);
    if (opts.search) navigate({ pathname: path, search: opts.search });
    else navigate(path);
  };

  const openModal = (setter) => {
    setSheetOpen(false);
    setTimeout(() => setter(true), 200);
  };

  const handleStartWorkoutHero = () => {
    setSheetOpen(false);
    if (activeWorkout) {
      resumeWorkout();
      navigate("/workouts");
    } else if (todayPlan) {
      startWorkout({
        ...todayPlan,
        plan_id: todayPlan.id || todayPlan._id,
        plan_name: todayPlan.name,
        exercises: todayPlan.exercises || [],
        total_exercises: (todayPlan.exercises || []).length,
      });
      navigate("/workouts");
    } else {
      navigate("/workouts");
    }
  };

  return (
    <>
      <nav
        className={`fixed bottom-0 left-0 right-0 bg-sidebar border-t border-sidebar-border z-40 ${
          forceVisible ? "" : "md:hidden"
        }`}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="bottom-nav"
      >
        <div className="relative flex items-center h-16">
          {/* Left 2 items */}
          <div className="flex flex-1 items-center justify-around">
            {leftItems.map(renderItem)}
          </div>

          {/* Center TA button - opens quick actions sheet */}
          <div className="relative flex items-center justify-center w-16 shrink-0 h-full">
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              onDragStart={(e) => e.preventDefault()}
              onContextMenu={(e) => e.preventDefault()}
              style={{
                WebkitTouchCallout: "none",
                boxShadow:
                  "0 0 0 8px rgba(56,189,248,0.06), 0 0 18px 6px rgba(56,189,248,0.18)",
              }}
              data-testid="bottomnav-ta-button"
              aria-label="Quick actions"
              className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center transition-all overflow-hidden select-none touch-none bg-sky-500 hover:bg-sky-400 active:scale-95"
            >
              <img
                src={BRAND_MONOGRAM}
                alt="TA"
                draggable="false"
                onDragStart={(e) => e.preventDefault()}
                className="w-10 h-10 object-contain pointer-events-none select-none"
                style={{
                  filter: "brightness(0) invert(1)",
                  WebkitUserDrag: "none",
                  WebkitTouchCallout: "none",
                }}
              />
            </button>
          </div>

          {/* Right 2 items */}
          <div className="flex flex-1 items-center justify-around">
            {rightItems.map(renderItem)}
          </div>
        </div>
      </nav>

      {/* TA FITNESS ACTION DECK — OPTION 2 REDESIGN */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="bottom"
          className="bg-[#080F1D] border-t border-white/10 text-white rounded-t-3xl max-h-[88vh] overflow-y-auto p-4 sm:p-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-2xl focus:outline-none"
          data-testid="quick-actions-sheet"
        >
          {/* Centered Drag Handle */}
          <div className="w-12 h-1.5 rounded-full bg-white/20 mx-auto mb-2" />

          {/* 1. Header: YOUR COMMAND CENTER · Quick Actions */}
          <SheetHeader className="text-left pt-0 pb-3 border-b border-white/10 pr-8">
            <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-sky-400">
              YOUR COMMAND CENTER
            </p>
            <SheetTitle className="text-lg sm:text-xl font-display font-black text-white tracking-tight flex items-center gap-1.5">
              <span>Quick Actions</span>
              <Zap className="w-4 h-4 text-sky-400 fill-sky-400/30" />
            </SheetTitle>
          </SheetHeader>

          <div className="mt-3.5 space-y-4 pb-2">
            {/* 2. FEATURED START / RESUME WORKOUT HERO */}
            <button
              type="button"
              onClick={handleStartWorkoutHero}
              data-testid="quick-action-start-workout"
              className="w-full text-left rounded-2xl p-4 sm:p-4.5 relative overflow-hidden group active:scale-[0.98] transition-all shadow-[0_0_20px_rgba(14,165,233,0.18)]"
              style={{
                background:
                  "radial-gradient(ellipse at top left, #0D2444 0%, #071324 100%)",
                border: "1px solid rgba(56, 189, 248, 0.4)",
              }}
            >
              <div className="absolute -top-12 -right-12 w-36 h-36 bg-sky-500/15 rounded-full blur-2xl pointer-events-none" />

              <div className="flex items-center justify-between mb-3 relative z-10">
                <span className="text-[10px] font-black uppercase tracking-widest text-sky-400 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
                  {activeWorkout ? "IN PROGRESS · RESUME" : "READY WHEN YOU ARE"}
                </span>
                <div className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-sky-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </div>

              <div className="flex items-center gap-3.5 relative z-10">
                <div className="w-12 h-12 rounded-xl bg-sky-500/20 border border-sky-400/40 text-sky-400 flex items-center justify-center shrink-0 shadow-[0_0_12px_rgba(56,189,248,0.3)]">
                  <Play className="w-6 h-6 fill-sky-400/30 text-sky-400 ml-0.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg sm:text-xl font-display font-black text-white uppercase tracking-tight truncate">
                    {activeWorkout ? "Resume Workout" : "Start Workout"}
                  </h3>
                  <p className="text-xs text-slate-300 truncate">
                    {activeWorkout
                      ? `Continue ${activeWorkout.plan_name || "session"}`
                      : "Jump straight into your training."}
                  </p>
                  <p className="text-[11px] font-semibold text-sky-400/90 pt-0.5 truncate">
                    {todayPlan?.name ? `Today: ${todayPlan.name}` : "Today's session"}
                  </p>
                </div>
              </div>
            </button>

            {/* 3. EVERYDAY ESSENTIALS — THREE-COLUMN GRID */}
            <div className="space-y-2">
              <div className="flex items-center justify-between px-0.5">
                <h4 className="text-xs sm:text-sm font-bold text-white tracking-wide">
                  Everyday Essentials
                </h4>
                <span className="text-[11px] text-slate-400 font-medium">
                  One-tap shortcuts
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                {/* Log Progress */}
                <button
                  type="button"
                  onClick={() => openModal(setProgressOpen)}
                  data-testid="quick-action-log-progress"
                  className="rounded-2xl p-3 sm:p-3.5 bg-[#0D182A] hover:bg-[#112038] border border-white/10 hover:border-sky-400/40 flex flex-col items-center justify-center text-center gap-2 group active:scale-95 transition-all shadow-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-400/30 text-sky-400 flex items-center justify-center group-hover:scale-105 transition-transform shadow-[0_0_10px_rgba(56,189,248,0.2)]">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <span className="text-xs font-bold text-white leading-tight">
                    Log Progress
                  </span>
                </button>

                {/* Log Meal */}
                <button
                  type="button"
                  onClick={() => openModal(setMealOpen)}
                  data-testid="quick-action-log-meal"
                  className="rounded-2xl p-3 sm:p-3.5 bg-[#0D182A] hover:bg-[#112038] border border-white/10 hover:border-sky-400/40 flex flex-col items-center justify-center text-center gap-2 group active:scale-95 transition-all shadow-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-400/30 text-sky-400 flex items-center justify-center group-hover:scale-105 transition-transform shadow-[0_0_10px_rgba(56,189,248,0.2)]">
                    <UtensilsCrossed className="w-5 h-5" />
                  </div>
                  <span className="text-xs font-bold text-white leading-tight">
                    Log Meal
                  </span>
                </button>

                {/* Library */}
                <button
                  type="button"
                  onClick={() => handleAction("/library")}
                  data-testid="quick-action-library"
                  className="rounded-2xl p-3 sm:p-3.5 bg-[#0D182A] hover:bg-[#112038] border border-white/10 hover:border-sky-400/40 flex flex-col items-center justify-center text-center gap-2 group active:scale-95 transition-all shadow-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-400/30 text-sky-400 flex items-center justify-center group-hover:scale-105 transition-transform shadow-[0_0_10px_rgba(56,189,248,0.2)]">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <span className="text-xs font-bold text-white leading-tight">
                    Library
                  </span>
                </button>
              </div>
            </div>

            {/* 4. DAILY CHECK-IN — ACHIEVEMENT STRIP */}
            <div className="space-y-1">
              {checkedInToday ? (
                /* After Check-In: Emerald Achievement Strip */
                <button
                  type="button"
                  onClick={() => openModal(setCheckInHistoryOpen)}
                  data-testid="quick-action-check-in"
                  className="w-full rounded-2xl p-3 sm:p-3.5 bg-gradient-to-r from-emerald-500/15 via-emerald-500/10 to-amber-500/10 border border-emerald-500/40 hover:border-emerald-400/60 shadow-[0_0_10px_rgba(16,185,129,0.15)] flex items-center justify-between gap-3 text-left active:scale-[0.99] transition-all group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 flex items-center justify-center shrink-0 shadow-[0_0_8px_rgba(16,185,129,0.25)]">
                      <Check className="w-5 h-5 stroke-[2.5]" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <h4 className="text-xs sm:text-sm font-bold text-white leading-tight">
                          Daily Check-In
                        </h4>
                        <span className="text-[10px] font-black uppercase text-amber-300">
                          ★ {streak}D
                        </span>
                      </div>
                      <p className="text-[11px] text-emerald-200/90 truncate">
                        Achievement unlocked · {streak} {streak === 1 ? "day" : "days"} streak
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      data-testid="quick-action-check-in-done-badge"
                      className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center gap-1"
                    >
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                      <span>Checked In</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-emerald-400/60 group-hover:text-emerald-300 group-hover:translate-x-0.5 transition-all" />
                  </div>
                </button>
              ) : !canCheckIn ? (
                /* Locked Check-In for non-eligible tier */
                <div
                  data-testid="quick-action-check-in-locked"
                  className="w-full rounded-2xl p-3 sm:p-3.5 bg-white/[0.03] border border-white/5 opacity-60 flex items-center justify-between gap-3 text-left cursor-not-allowed"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 text-slate-400 flex items-center justify-center shrink-0">
                      <Lock className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs sm:text-sm font-bold text-white/80 leading-tight">
                        Daily Check-In
                      </h4>
                      <p className="text-[11px] text-white/45 truncate">
                        Elite Coaching only
                      </p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-slate-400 bg-white/5 px-2 py-0.5 rounded-full border border-white/10">
                    Locked
                  </span>
                </div>
              ) : (
                /* Before Check-In: Electric Blue Strip */
                <button
                  type="button"
                  onClick={() => handleAction("/", { search: "?action=check-in" })}
                  data-testid="quick-action-check-in"
                  className="w-full rounded-2xl p-3 sm:p-3.5 bg-sky-500/10 hover:bg-sky-500/15 border border-sky-400/30 hover:border-sky-400/50 shadow-[0_0_12px_rgba(56,189,248,0.15)] flex items-center justify-between gap-3 text-left active:scale-[0.99] transition-all group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-sky-500/20 border border-sky-400/40 text-sky-400 flex items-center justify-center shrink-0 shadow-[0_0_8px_rgba(56,189,248,0.25)]">
                      <Flame className="w-5 h-5 fill-sky-400/30" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs sm:text-sm font-bold text-white leading-tight">
                        Daily Check-In
                      </h4>
                      <p className="text-[11px] text-sky-200/80 truncate">
                        Keep your streak alive · Tap to claim
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-sky-400/70 group-hover:text-sky-300 group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>
              )}
            </div>

            {/* 5. PREMIUM COACHING SECTION */}
            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between px-0.5">
                <div className="flex items-center gap-1.5">
                  <Crown className="w-3.5 h-3.5 text-amber-400" />
                  <h4 className="text-xs sm:text-sm font-bold text-white tracking-wide">
                    Premium Coaching
                  </h4>
                </div>
                <span className="text-[9px] font-black uppercase tracking-wider text-amber-200 bg-amber-500/15 border border-amber-400/30 px-2 py-0.5 rounded-full">
                  PREMIUM
                </span>
              </div>

              <div className="rounded-2xl bg-[#0D182A] border border-amber-500/30 overflow-hidden divide-y divide-white/5 shadow-sm">
                {/* 1. Trainer Sessions / Check-in */}
                {canBookSessions ? (
                  <button
                    type="button"
                    onClick={() => handleAction("/sessions")}
                    data-testid="quick-action-book-session"
                    className="w-full p-3 sm:p-3.5 flex items-center justify-between gap-3 text-left hover:bg-white/5 active:scale-[0.99] transition group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-400/30 text-amber-300 flex items-center justify-center shrink-0">
                        <CalendarPlus className="w-4.5 h-4.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-semibold text-white leading-tight truncate">
                          Trainer Sessions / Check-in
                        </p>
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">
                          Book & manage sessions
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0" />
                  </button>
                ) : (
                  <div
                    data-testid="quick-action-book-session-locked"
                    className="w-full p-3 sm:p-3.5 flex items-center justify-between gap-3 text-left opacity-60 cursor-not-allowed"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 text-white/40 flex items-center justify-center shrink-0">
                        <CalendarPlus className="w-4.5 h-4.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-semibold text-white/70 leading-tight truncate">
                          Trainer Sessions / Check-in
                        </p>
                        <p className="text-[11px] text-white/40 truncate mt-0.5 flex items-center gap-1">
                          <Lock className="w-2.5 h-2.5" /> Strength or Elite Coaching only
                        </p>
                      </div>
                    </div>
                    <Lock className="w-3.5 h-3.5 text-white/30 shrink-0" />
                  </div>
                )}

                {/* 2. My Trainer */}
                {canBookSessions ? (
                  <button
                    type="button"
                    onClick={() => handleAction("/my-trainer")}
                    data-testid="quick-action-my-trainer"
                    className="w-full p-3 sm:p-3.5 flex items-center justify-between gap-3 text-left hover:bg-white/5 active:scale-[0.99] transition group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-400/30 text-amber-300 flex items-center justify-center shrink-0">
                        <UserCheck className="w-4.5 h-4.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-semibold text-white leading-tight truncate">
                          My Trainer
                        </p>
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">
                          Connect with your coach
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0" />
                  </button>
                ) : (
                  <div
                    data-testid="quick-action-my-trainer-locked"
                    className="w-full p-3 sm:p-3.5 flex items-center justify-between gap-3 text-left opacity-60 cursor-not-allowed"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 text-white/40 flex items-center justify-center shrink-0">
                        <UserCheck className="w-4.5 h-4.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-semibold text-white/70 leading-tight truncate">
                          My Trainer
                        </p>
                        <p className="text-[11px] text-white/40 truncate mt-0.5 flex items-center gap-1">
                          <Lock className="w-2.5 h-2.5" /> Strength or Elite Coaching only
                        </p>
                      </div>
                    </div>
                    <Lock className="w-3.5 h-3.5 text-white/30 shrink-0" />
                  </div>
                )}

                {/* 3. Ask AI Coach */}
                {canBookSessions ? (
                  <button
                    type="button"
                    onClick={() => handleAction("/ai-coach")}
                    data-testid="quick-action-ai-coach"
                    className="w-full p-3 sm:p-3.5 flex items-center justify-between gap-3 text-left hover:bg-white/5 active:scale-[0.99] transition group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-400/30 text-amber-300 flex items-center justify-center shrink-0">
                        <Sparkles className="w-4.5 h-4.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-semibold text-white leading-tight truncate">
                          Ask AI Coach
                        </p>
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">
                          Personalized fitness guidance
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0" />
                  </button>
                ) : (
                  <div
                    data-testid="quick-action-ai-coach-locked"
                    className="w-full p-3 sm:p-3.5 flex items-center justify-between gap-3 text-left opacity-60 cursor-not-allowed"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 text-white/40 flex items-center justify-center shrink-0">
                        <Sparkles className="w-4.5 h-4.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-semibold text-white/70 leading-tight truncate">
                          Ask AI Coach
                        </p>
                        <p className="text-[11px] text-white/40 truncate mt-0.5 flex items-center gap-1">
                          <Lock className="w-2.5 h-2.5" /> Strength or Elite Coaching only
                        </p>
                      </div>
                    </div>
                    <Lock className="w-3.5 h-3.5 text-white/30 shrink-0" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Quick-action modals mounted at nav so they work from any page */}
      {progressOpen && (
        <QuickProgressDialog
          open={progressOpen}
          onOpenChange={setProgressOpen}
          user={user}
        />
      )}

      <Dialog open={mealOpen} onOpenChange={setMealOpen}>
        <DialogContent className="max-w-md" data-testid="quick-meal-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Log a meal</DialogTitle>
            <DialogDescription>
              {canUseAIMacros
                ? "We'll estimate the macros for you. Tweak any value before saving."
                : "Enter the food, portion and macros below."}
            </DialogDescription>
          </DialogHeader>
          {user && (
            <QuickMealLogForm
              userId={user.id}
              todayDate={todayDate}
              compact
              onSaved={() => {
                queryClient.invalidateQueries({
                  queryKey: ["meal-logs", todayDate, user.id],
                });
                setMealOpen(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Check-In History Calendar Modal */}
      {checkInHistoryOpen && (
        <CheckInCalendarModal
          open={checkInHistoryOpen}
          onOpenChange={setCheckInHistoryOpen}
          checkedDates={checkedDates}
        />
      )}
    </>
  );
}
